import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { clubInfo, faqData } from '../data/content';
import SEO from '../components/SEO';
import EventStructuredData from '../components/EventStructuredData';
import FAQStructuredData from '../components/FAQStructuredData';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dogName: '',
    dogBreed: '',
    message: ''
  });

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Doppelklick verhindern
    setFormError(null);
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://intern.hsv-adlershof.berlin';
      const response = await fetch(`${apiUrl}/api/waitlist/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setFormSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          dogName: '',
          dogBreed: '',
          message: ''
        });
      } else {
        setFormError(result.message || 'Es gab einen Fehler beim Senden Ihrer Nachricht.');
      }
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setFormError('Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const getNextSaturday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    return nextSaturday;
  };

  const nextSaturday = getNextSaturday();
  const guestTrainingEvent = {
    name: "Gästetraining HSV Berlin-Adlershof",
    description: "Gästetraining für Interessierte. Lernen Sie unseren Verein kennen und trainieren Sie mit uns. Jeden Samstag am Birkenweg in Berlin-Adlershof. Aktuell Warteliste.",
    startDate: `${nextSaturday.toISOString().split('T')[0]}T09:30:00+02:00`,
    endDate: `${nextSaturday.toISOString().split('T')[0]}T11:00:00+02:00`,
    location: "HSV Berlin-Adlershof e.V. Trainingsplatz",
    price: "0"
  };

  const contactItems = [
    {
      icon: MapPinIcon,
      label: 'Adresse',
      value: clubInfo.contact.address,
      href: 'https://www.google.com/maps/search/?api=1&query=Hundesportverein+Berlin-Adlershof+Birkenweg+12439+Berlin',
      external: true
    },
    {
      icon: PhoneIcon,
      label: 'Telefon',
      value: clubInfo.contact.phone,
      href: `tel:${clubInfo.contact.phone.replace(/\s|\//g, '')}`
    },
    {
      icon: EnvelopeIcon,
      label: 'E-Mail',
      value: clubInfo.contact.email,
      href: `mailto:${clubInfo.contact.email}`
    },
    {
      icon: UserIcon,
      label: 'Ansprechpartner',
      value: clubInfo.contact.contactPerson
    }
  ];

  const steps = [
    {
      icon: PaperAirplaneIcon,
      title: 'Anmeldung',
      description: 'Schick uns über das Formular eine kurze Nachricht mit Infos zu dir und deinem Hund.'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Wir melden uns',
      description: 'Sobald ein Platz frei ist, kontaktieren wir dich für einen Termin am Samstag.'
    },
    {
      icon: CheckCircleIcon,
      title: 'Gästetraining',
      description: 'Du nimmst am regulären Samstagstraining teil und lernst Verein, Trainer und Gruppe kennen.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO
        title="Kontakt"
        description="Kontakt HSV Berlin-Adlershof: Birkenweg, 12439 Berlin. Gästetraining samstags – aktuell Warteliste. Telefon, E-Mail & Anfahrt."
        url="/kontakt"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Kontakt', url: '/kontakt' }
        ]}
      />
      <EventStructuredData events={[guestTrainingEvent]} />
      <FAQStructuredData faqs={faqData} />

      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/facilities/clubhouse.jpg"
            alt="HSV Vereinshaus"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl"
            >
              <h1 className="text-3xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                KONTAKT
              </h1>
              <p className="text-base md:text-xl text-white/90">
                Schreib uns – wir freuen uns auf dich und deinen Hund.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Waitlist Alert Bar */}
      <section className="bg-amber-100 dark:bg-amber-900/20 border-b-4 border-amber-400 dark:border-amber-600">
        <div className="container mx-auto px-4 max-w-5xl py-5 md:py-6">
          <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-400 dark:bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5 md:w-6 md:h-6 text-amber-950" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-base md:text-lg text-amber-950 dark:text-amber-100 leading-tight">
                  Aktuell nur Warteliste – Samstagsgruppe ausgebucht
                </p>
                <p className="text-sm md:text-base text-amber-900 dark:text-amber-200/90 mt-1">
                  Gästetraining bieten wir ausschließlich samstags an. Trag dich gerne über das Formular auf die Warteliste ein.
                </p>
              </div>
            </div>
            <a
              href="#anmeldung"
              className="bg-amber-950 hover:bg-amber-900 dark:bg-amber-100 dark:hover:bg-amber-200 dark:text-amber-950 text-amber-50 px-5 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 whitespace-nowrap w-full md:w-auto justify-center"
            >
              Auf Warteliste
              <ArrowRightIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Info + Map */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-5 gap-8 md:gap-12 items-stretch">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                So erreichst du uns
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6 md:mb-8">
                Birkenweg, mitten in Berlin-Adlershof.
              </p>

              <ul className="divide-y divide-neutral-200 dark:divide-neutral-800 border-y border-neutral-200 dark:border-neutral-800">
                {contactItems.map((item, idx) => {
                  const Icon = item.icon;
                  const content = (
                    <div className="flex items-center gap-4 py-4 group">
                      <div className="w-11 h-11 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/40 transition-colors">
                        <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wide font-semibold text-neutral-500 dark:text-neutral-500 mb-0.5">
                          {item.label}
                        </p>
                        <p className={`text-sm md:text-base font-medium text-neutral-900 dark:text-neutral-100 break-words ${item.href ? 'group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors' : ''}`}>
                          {item.value}
                        </p>
                      </div>
                      {item.href && (
                        <ArrowRightIcon className="w-4 h-4 text-neutral-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                      )}
                    </div>
                  );
                  return (
                    <li key={idx}>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.external ? '_blank' : undefined}
                          rel={item.external ? 'noopener noreferrer' : undefined}
                        >
                          {content}
                        </a>
                      ) : (
                        content
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 min-h-[320px] md:min-h-[400px] rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-sm"
            >
              <iframe
                src="https://maps.google.com/maps?q=Birkenweg+12439+Berlin+Hundesportverein&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '320px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="HSV Berlin-Adlershof – Birkenweg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 md:py-16 lg:py-20 bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-14"
          >
            <div className="inline-flex items-center gap-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <CalendarDaysIcon className="w-4 h-4" />
              Nur samstags
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-3 md:mb-4">
              So läuft das Gästetraining
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Wir bieten Gästetraining ausschließlich samstags an. Aktuell ist die Gruppe ausgebucht – über die Warteliste rückst du nach.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-white dark:bg-surface-dark rounded-2xl p-6 md:p-7 border border-neutral-200 dark:border-neutral-800"
                >
                  <div className="absolute -top-3 -left-3 w-9 h-9 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                    {index + 1}
                  </div>
                  <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 md:mt-10 flex items-center justify-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            <span>Hinweis: Agility pausiert momentan.</span>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="anmeldung" className="py-12 md:py-16 lg:py-20 scroll-mt-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-10"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-3 md:mb-4">
              Auf die Warteliste setzen
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
              Erzähl uns kurz von dir und deinem Hund – wir melden uns, sobald ein Platz frei wird.
            </p>
          </motion.div>

          {formSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl p-8 md:p-10 text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                Vielen Dank!
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Wir haben dich auf die Warteliste gesetzt und eine Bestätigungs-E-Mail gesendet.
              </p>
              <button
                onClick={() => setFormSubmitted(false)}
                className="text-primary-600 dark:text-primary-400 hover:underline font-semibold text-sm"
              >
                Neue Nachricht schreiben
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white dark:bg-surface-dark rounded-2xl p-6 md:p-8 border border-neutral-200 dark:border-neutral-800 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                    Dein Name <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="input w-full"
                    placeholder="Max Mustermann"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                    E-Mail <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="input w-full"
                    placeholder="max@beispiel.de"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="+49 123 456789"
                  />
                </div>
                <div>
                  <label htmlFor="dogBreed" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                    Hunderasse
                  </label>
                  <input
                    type="text"
                    id="dogBreed"
                    name="dogBreed"
                    value={formData.dogBreed}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="z.B. Labrador"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="dogName" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                  Name deines Hundes
                </label>
                <input
                  type="text"
                  id="dogName"
                  name="dogName"
                  value={formData.dogName}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Bello"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
                  Deine Nachricht <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  required
                  className="input w-full resize-y"
                  placeholder="Anmeldung zur Warteliste fürs Gästetraining – Alter und Rasse deines Hundes, eure bisherige Erfahrung mit Hundesport …"
                />
              </div>

              {formError && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Wird gesendet …
                  </>
                ) : (
                  <>
                    Nachricht senden
                    <PaperAirplaneIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-12"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-3 md:mb-4">
              Häufig gestellte Fragen
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
              Antworten auf die wichtigsten Fragen rund um Gästetraining und Mitgliedschaft.
            </p>
          </motion.div>

          <div className="bg-white dark:bg-surface-dark rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
            {faqData.map((faq, index) => {
              const isOpen = expandedFaq === index;
              return (
                <div
                  key={index}
                  className={index !== 0 ? 'border-t border-neutral-200 dark:border-neutral-800' : ''}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                    className="w-full px-5 md:px-7 py-5 md:py-6 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/30 transition-colors"
                  >
                    <div className="flex justify-between items-center gap-4">
                      <h3 className="text-base md:text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                        {faq.question}
                      </h3>
                      <ChevronDownIcon
                        className={`w-5 h-5 flex-shrink-0 text-primary-600 dark:text-primary-400 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="px-5 md:px-7 pb-5 md:pb-6"
                    >
                      <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400 whitespace-pre-line leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
